import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import FinDataGrid from "../components/findatagrid";
import AdminAiProviders, { describeTestResult, formatTestResult, readErrorMessage } from "./adminAiProviders";
import AdminAiTasks from "./adminAiTasks";

const WEIGHTS = [1, 2, 3, 4, 5];
const PROBE_TYPES = ["SCREENER", "ANALYSIS", "TECHNICALS"];

const API_BASE = process.env.REACT_APP_API_URI;

const isActiveModel = (row) => (row.status || "active") === "active";

const formatDateTime = (value) => (value ? new Date(value).toLocaleString("de-DE") : "");

// Schlüssel fürs lokale Modell und die festen Provider ist der Name selbst. Bei allen anderen
// kommt der Provider davor, damit dasselbe Modell bei zwei Providern existieren kann.
const FIXED_KEY_PROVIDERS = new Set(["openai", "mistral", "deepseek"]);
const suggestModelKey = (provider, remoteModel) => (
  FIXED_KEY_PROVIDERS.has(provider?.name) || provider?.is_local ? remoteModel : `${provider?.name}:${remoteModel}`
);

const AdminTabAiModels = () => {
  const [models, setModels] = useState([]);
  const [providers, setProviders] = useState([]);
  const [taskRefreshKey, setTaskRefreshKey] = useState(0);
  const [showArchived, setShowArchived] = useState(false);
  const [modelError, setModelError] = useState(null);
  const [modelDialogError, setModelDialogError] = useState(null);

  const activeModels = models.filter(isActiveModel);
  const visibleModels = showArchived ? models : activeModels;

  // Model dialog
  const [modelDialogOpen, setModelDialogOpen] = useState(false);
  const [modelDialogMode, setModelDialogMode] = useState("add"); // "add" | "edit"
  const [editModel, setEditModel] = useState({ model: "", apiModel: "", weight: 3, llmProvider: "llmstudio" });
  const [remoteModels, setRemoteModels] = useState(null);
  const [loadingRemote, setLoadingRemote] = useState(false);
  const [probeType, setProbeType] = useState("SCREENER");
  const [probeIdentifier, setProbeIdentifier] = useState("");
  const [probing, setProbing] = useState(false);
  const [probeResult, setProbeResult] = useState(null);

  const activeProviders = providers.filter((p) => p.status === "active");

  async function fetchModels() {
    try {
      const response = await fetch(API_BASE + "/api/ai_model_weights?includeArchived=1");
      if (!response.ok) {
        setModelError(await readErrorMessage(response));
        return;
      }
      setModels(await response.json());
    } catch (error) {
      console.error(error);
      setModelError(error.message);
    }
  }

  async function fetchProviders() {
    try {
      const response = await fetch(API_BASE + "/api/ai_providers?includeArchived=1");
      if (!response.ok) {
        setModelError(await readErrorMessage(response));
        return;
      }
      setProviders(await response.json());
    } catch (error) {
      console.error(error);
      setModelError(error.message);
    }
  }

  useEffect(() => {
    fetchProviders();
    fetchModels();
  }, []);

  // --- Model CRUD ---
  const openModelDialog = (mode, values) => {
    setModelDialogMode(mode);
    setEditModel(values);
    setModelDialogError(null);
    setRemoteModels(null);
    setProbeResult(null);
    setProbeIdentifier("");
    setModelDialogOpen(true);
  };

  const handleAddModel = () => {
    openModelDialog("add", { model: "", apiModel: "", weight: 3, llmProvider: "llmstudio" });
  };

  const handleEditModel = (row) => {
    openModelDialog("edit", {
      model: row.model,
      apiModel: row.api_model || "",
      weight: row.weight,
      llmProvider: row.llmprovider || row.llmProvider,
    });
  };

  const selectedProvider = providers.find((p) => p.name === editModel.llmProvider);

  const handleLoadRemoteModels = async () => {
    if (!selectedProvider) return;
    setLoadingRemote(true);
    setModelDialogError(null);
    setRemoteModels(null);
    try {
      const response = await fetch(`${API_BASE}/api/ai_providers/${selectedProvider.id}/remote-models`);
      if (!response.ok) {
        setModelDialogError(await readErrorMessage(response));
        return;
      }
      setRemoteModels((await response.json()).models);
    } catch (error) {
      setModelDialogError(error.message);
    } finally {
      setLoadingRemote(false);
    }
  };

  const handlePickRemoteModel = (remoteModel) => {
    const key = modelDialogMode === "add" ? suggestModelKey(selectedProvider, remoteModel) : editModel.model;
    setEditModel({ ...editModel, model: key, apiModel: key === remoteModel ? "" : remoteModel });
  };

  const handleProbe = async () => {
    setProbing(true);
    setProbeResult(null);
    setModelDialogError(null);
    try {
      const response = await fetch(`${API_BASE}/api/ai_model_weights/${encodeURIComponent(editModel.model)}/probe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type: probeType, identifier: probeIdentifier.trim() || undefined }),
      });
      if (!response.ok) {
        setModelDialogError(await readErrorMessage(response));
        return;
      }
      setProbeResult(await response.json());
      fetchModels();
    } catch (error) {
      setModelDialogError(error.message);
    } finally {
      setProbing(false);
    }
  };

  // Führt eine Modell-Aktion aus. Lehnt das Backend ab (z. B. 409), steht der Grund über der Tabelle.
  const runModelAction = async (path, method) => {
    setModelError(null);
    try {
      const response = await fetch(API_BASE + "/api/ai_model_weights/" + path, { method });
      if (!response.ok) {
        setModelError(await readErrorMessage(response));
      }
      fetchModels();
      setTaskRefreshKey((key) => key + 1);
    } catch (error) {
      console.error(error);
      setModelError(error.message);
    }
  };

  const handleDeleteModel = (model) => {
    if (!window.confirm(`Modell "${model}" wirklich löschen?`)) return;
    runModelAction(encodeURIComponent(model), "DELETE");
  };

  const handleArchiveModel = (model) => {
    if (!window.confirm(`Modell "${model}" archivieren? Es verschwindet aus den Auswahllisten, Ranking und Historie bleiben unverändert.`)) return;
    runModelAction(encodeURIComponent(model) + "/archive", "POST");
  };

  const handleRestoreModel = (model) => {
    runModelAction(encodeURIComponent(model) + "/restore", "POST");
  };

  const handleSaveModel = async () => {
    const url = modelDialogMode === "add"
      ? API_BASE + "/api/ai_model_weights"
      : API_BASE + "/api/ai_model_weights/" + encodeURIComponent(editModel.model);
    const method = modelDialogMode === "add" ? "POST" : "PUT";

    setModelDialogError(null);
    try {
      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: editModel.model,
          apiModel: editModel.apiModel,
          weight: editModel.weight,
          llmProvider: editModel.llmProvider,
        }),
      });
      if (!response.ok) {
        setModelDialogError(await readErrorMessage(response));
        return;
      }
      setModelDialogOpen(false);
      fetchModels();
      fetchProviders();
      setTaskRefreshKey((key) => key + 1);
    } catch (error) {
      console.error(error);
      setModelDialogError(error.message);
    }
  };

  // --- Column Definitions ---
  const modelColumns = [
    { field: "model", headerName: "Modell", width: 240, cellClassName: "name-column--cell" },
    { field: "weight", headerName: "Gewicht", width: 100 },
    {
      field: "llmprovider",
      headerName: "Provider",
      width: 140,
      valueGetter: (params) => params.row.llmprovider || params.row.llmProvider,
    },
    { field: "api_model", headerName: "Name beim Provider", width: 200 },
    {
      field: "last_test",
      headerName: "Letzter Test",
      width: 150,
      valueGetter: (params) => formatTestResult(params.row),
      renderCell: (params) => (
        <Tooltip title={describeTestResult(params.row)}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 100,
      valueGetter: (params) => (isActiveModel(params.row) ? "aktiv" : "archiviert"),
    },
    {
      field: "editBtn",
      headerName: "Edit",
      width: 80,
      renderCell: (params) => (
        <Button color="inherit" size="small" onClick={() => handleEditModel(params.row)}>
          Edit
        </Button>
      ),
    },
    {
      field: "archiveBtn",
      headerName: "Archiv",
      width: 130,
      renderCell: (params) => {
        if (!isActiveModel(params.row)) {
          return (
            <Button color="inherit" size="small" onClick={() => handleRestoreModel(params.row.model)}>
              Wiederherstellen
            </Button>
          );
        }
        const inUse = (params.row.jobs || []).length > 0;
        return (
          <Tooltip title={inUse ? "Wird noch von einem Job genutzt" : ""}>
            <span>
              <Button color="inherit" size="small" disabled={inUse} onClick={() => handleArchiveModel(params.row.model)}>
                Archivieren
              </Button>
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: "deleteBtn",
      headerName: "Delete",
      width: 80,
      renderCell: (params) => {
        const deletable = !params.row.response_count && (params.row.jobs || []).length === 0;
        if (!deletable) return null;
        return (
          <Button color="error" size="small" onClick={() => handleDeleteModel(params.row.model)}>
            Del
          </Button>
        );
      },
    },
    { field: "response_count", headerName: "Analysen", width: 90, type: "number" },
    {
      field: "last_used",
      headerName: "Letzte Nutzung",
      width: 170,
      renderCell: (params) => formatDateTime(params.value),
    },
    {
      field: "jobs",
      headerName: "Jobs",
      width: 260,
      valueGetter: (params) => (params.row.jobs || []).map((job) => job.name).join(", "),
      renderCell: (params) => (
        <Tooltip title={params.value || ""}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box mt={2}>
      <AdminAiProviders
        providers={providers}
        onChanged={() => {
          fetchProviders();
          fetchModels();
          setTaskRefreshKey((key) => key + 1);
        }}
      />

      {/* Bereich A: AI Modelle & Gewichtung */}
      <Box mt={4} display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="h5">AI Modelle & Gewichtung</Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <FormControlLabel
            control={<Checkbox checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />}
            label="Archivierte anzeigen"
          />
          <Button variant="outlined" color="secondary" onClick={handleAddModel}>
            Modell hinzufügen
          </Button>
        </Box>
      </Box>
      {modelError && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setModelError(null)}>
          {modelError}
        </Alert>
      )}
      <FinDataGrid
        id="gridModels"
        height="35vh"
        density="compact"
        rows={visibleModels}
        columns={modelColumns}
        getRowId={(row) => row.model}
      />

      {/* Bereich B: Aufgaben, Modellwechsel und Verlauf */}
      <AdminAiTasks
        models={models}
        providers={providers}
        refreshKey={taskRefreshKey}
        onChanged={fetchModels}
      />

      {/* Model Dialog */}
      <Dialog open={modelDialogOpen} onClose={() => setModelDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{modelDialogMode === "add" ? "Modell hinzufügen" : "Modell bearbeiten"}</DialogTitle>
        <DialogContent>
          {modelDialogError && <Alert severity="error" sx={{ mb: 1 }}>{modelDialogError}</Alert>}
          <FormControl fullWidth variant="standard" margin="dense">
            <InputLabel>Provider</InputLabel>
            <Select
              value={editModel.llmProvider}
              onChange={(e) => {
                setEditModel({ ...editModel, llmProvider: e.target.value });
                setRemoteModels(null);
              }}
            >
              {!activeProviders.some((p) => p.name === editModel.llmProvider) && (
                <MenuItem value={editModel.llmProvider}>{editModel.llmProvider} (nicht aktiv)</MenuItem>
              )}
              {activeProviders.map((p) => (
                <MenuItem key={p.name} value={p.name}>{p.label || p.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box display="flex" alignItems="center" gap={2} mt={1}>
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
              disabled={!selectedProvider || selectedProvider.status !== "active" || loadingRemote}
              onClick={handleLoadRemoteModels}
            >
              {loadingRemote ? <CircularProgress size={18} /> : "Modelle vom Provider laden"}
            </Button>
            {remoteModels && (
              <FormControl fullWidth variant="standard">
                <InputLabel>{`Auswahl (${remoteModels.length})`}</InputLabel>
                <Select value="" onChange={(e) => handlePickRemoteModel(e.target.value)}>
                  {remoteModels.map((name) => (
                    <MenuItem key={name} value={name}>{name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
          <TextField
            autoFocus
            margin="dense"
            label="Modell (Schlüssel)"
            fullWidth
            variant="standard"
            value={editModel.model}
            disabled={modelDialogMode === "edit"}
            helperText="Unter diesem Namen stehen Analysen und Jobs. Nach dem Anlegen nicht änderbar."
            onChange={(e) => setEditModel({ ...editModel, model: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Name beim Provider (optional)"
            fullWidth
            variant="standard"
            value={editModel.apiModel}
            helperText="Leer: Der Schlüssel geht unverändert an den Provider."
            onChange={(e) => setEditModel({ ...editModel, apiModel: e.target.value })}
          />
          <FormControl fullWidth variant="standard" margin="dense">
            <InputLabel>Gewicht</InputLabel>
            <Select
              value={editModel.weight}
              onChange={(e) => setEditModel({ ...editModel, weight: e.target.value })}
            >
              {WEIGHTS.map((w) => (
                <MenuItem key={w} value={w}>{w}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Alert severity="info" sx={{ mt: 1 }}>
            Das Gewicht zählt im Ranking, bei MCP und in der Phase-3-Auswahl. Ab Gewicht 4 gilt ein Papier dort schon als vom Premium-Modell geprüft.
          </Alert>
          {modelDialogMode === "edit" && (
            <Box mt={2}>
              <Typography variant="subtitle2">Probe mit echtem Prompt, nichts wird gespeichert</Typography>
              <Box display="flex" alignItems="flex-end" gap={2}>
                <FormControl variant="standard" sx={{ minWidth: 140 }}>
                  <InputLabel>Typ</InputLabel>
                  <Select value={probeType} onChange={(e) => setProbeType(e.target.value)}>
                    {PROBE_TYPES.map((t) => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Papier (leer: außerhalb des Portfolios)"
                  variant="standard"
                  fullWidth
                  value={probeIdentifier}
                  onChange={(e) => setProbeIdentifier(e.target.value)}
                />
                <Button variant="outlined" color="secondary" disabled={probing} onClick={handleProbe}>
                  {probing ? <CircularProgress size={20} /> : "Probe"}
                </Button>
              </Box>
              {probeResult && (
                <Alert severity={probeResult.ok ? "success" : "error"} sx={{ mt: 1 }}>
                  {probeResult.identifier}, {probeResult.type}: {probeResult.latency_ms} ms
                  {probeResult.ok ? `, ${probeResult.length} Zeichen, Score ${probeResult.score ?? "nicht gefunden"}, ${probeResult.short_response}` : `, ${probeResult.message}`}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setModelDialogOpen(false)}>Cancel</Button>
          <Button color="inherit" onClick={handleSaveModel}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminTabAiModels;
